import { KEYS } from "@platejs/utils";
import isUrl from "is-url";

import type {
	Note,
	NoteBlock,
	NoteInfo,
	NotePopulated,
	PlateBulletedListElement,
	PlateListItemChildElement,
	PlateListItemElement,
	PlateNumberedListElement,
	PlateTableElement,
	PlateCodeBlockElement,
} from "@vita-learning/types";

import { decodeHtml, EnhancedError, isHexId, isOfType } from "~/shared/utils";

import { NOTE_SOURCES } from "./constants";

interface HasChildren {
	readonly children: Record<string, unknown>[];
}

enum NoteErrorId {
	USER = "NOTE_USER",
	TITLE = "NOTE_TITLE",
	SOURCE = "NOTE_SOURCE",
	FILE = "NOTE_FILE",
	URL = "NOTE_URL",
	CONTENT = "NOTE_CONTENT",
}

const isNoteSource = (source: unknown): source is Note["source"] =>
	NOTE_SOURCES.some(s => s === source);

export function assertsNoteInfo(
	note: unknown,
	file?: File
): asserts note is NoteInfo {
	if (!note || typeof note !== "object")
		throw new Error("Note must be an object");

	if ("id" in note && !isHexId(note.id))
		throw new Error("Note requires a valid ID");

	if (!("userId" in note) || !isHexId(note.userId))
		throw new Error("Note requires a valid user ID");

	if (!("title" in note) || !note.title)
		throw new EnhancedError("Title is a required field", NoteErrorId.TITLE);

	if (typeof note.title !== "string")
		throw new EnhancedError("Title must be a string", NoteErrorId.TITLE);

	if (!("content" in note))
		throw new EnhancedError("Content is required", NoteErrorId.CONTENT);

	if (!Array.isArray(note.content))
		throw new EnhancedError(
			"Content must be an array of blocks",
			NoteErrorId.CONTENT
		);

	for (const block of note.content) {
		if (!("type" in block) || typeof block.type !== "string")
			throw new EnhancedError("Block type is required", NoteErrorId.CONTENT);

		if (!("children" in block) || !Array.isArray(block.children))
			throw new EnhancedError(
				"Block children is required",
				NoteErrorId.CONTENT
			);
	}

	if (!("source" in note) || !isNoteSource(note.source))
		throw new EnhancedError("Invalid source", NoteErrorId.SOURCE);

	if (note.source === "file" && !file)
		throw new EnhancedError(
			"A file is required when Source is 'File'",
			NoteErrorId.FILE
		);

	if (note.source === "prompt") {
		if (!("prompt" in note) || !note.prompt)
			throw new EnhancedError(
				"Prompt is required when Source is 'Prompt'",
				NoteErrorId.SOURCE
			);

		if (typeof note.prompt !== "string")
			throw new EnhancedError("Prompt must be a string", NoteErrorId.SOURCE);
	}

	if (note.source === "url") {
		if (!("url" in note))
			throw new EnhancedError(
				"A URL is required when Source is 'URL'",
				NoteErrorId.URL
			);

		if (typeof note.url !== "string" || !isUrl(note.url))
			throw new EnhancedError("URL must be a valid URL", NoteErrorId.URL);
	}
}

export function assertsNote(note: unknown, file?: File): asserts note is Note {
	assertsNoteInfo(note, file);

	if (!("id" in note) || !isHexId(note.id))
		throw new Error("Note requires a valid ID");
}

export const isValidNoteInfo = (
	note: unknown,
	file?: File
): note is NoteInfo => {
	try {
		assertsNoteInfo(note, file);
		return true;
	} catch {
		return false;
	}
};

export const isValidNote = (note: unknown, file?: File): note is Note => {
	try {
		assertsNote(note, file);
		return true;
	} catch {
		return false;
	}
};

const decodeTextNode = <T extends Record<string, unknown>>(child: T) => {
	if ("text" in child)
		return !isOfType(child.text, "string")
			? child
			: {
					...child,
					text: decodeHtml(child.text),
				};

	if ("url" in child)
		return !isOfType(child.url, "string")
			? child
			: {
					...child,
					url: decodeHtml(child.url),
				};

	return child;
};

const decodeNestedTextNode = <T extends HasChildren>({
	children = [],
	...rest
}: T) => ({
	...rest,
	children: children.map(decodeTextNode),
});

const decodeListNode = (li: PlateListItemElement): PlateListItemElement => ({
	...li,
	children: li.children.map(lic => {
		if (lic.type === "lic")
			return {
				...lic,
				children: lic.children.map(decodeTextNode),
			};

		return {
			...lic,
			children: lic.children.map(decodeListNode),
		};
	}) as [
		PlateListItemChildElement,
		...(PlateBulletedListElement | PlateNumberedListElement)[],
	],
});

const decodeNoteContent = (block: NoteBlock) => {
	let children: unknown;

	switch (block.type) {
		case KEYS.p:
		case KEYS.h1:
		case KEYS.h2:
		case KEYS.h3:
		case KEYS.h4:
		case KEYS.h5:
		case KEYS.h6:
		case KEYS.link:
		case KEYS.blockquote:
		case KEYS.codeLine:
		case KEYS.listTodoClassic: {
			children = block.children.map(decodeTextNode);
			break;
		}
		case KEYS.ulClassic:
		case KEYS.olClassic: {
			const block_ = Object.assign({}, block) as
				| PlateBulletedListElement
				| PlateNumberedListElement;
			children = block_.children.map(decodeListNode);
			break;
		}
		case KEYS.codeBlock: {
			const block_ = Object.assign({}, block) as PlateCodeBlockElement;
			children = block_.children.map(decodeNestedTextNode);
			break;
		}
		case KEYS.table: {
			const block_ = Object.assign({}, block) as PlateTableElement;
			children = block_.children.map(row =>
				row.children.map(cell => cell.children.map(decodeNestedTextNode))
			);
			break;
		}
		// For these types, there are no text nodes to decode, so we can return the block as is
		case KEYS.hr:
		case KEYS.mediaEmbed:
		case KEYS.img:
		default:
			return block;
	}

	return {
		...block,
		children,
	} as NoteBlock;
};

/**
 * Safely remove encoded HTML entities from the note's content.
 * @param {NotePopulated} note note to decode
 * @returns {NotePopulated} note with decoded content
 */
export const decodeNote = ({
	content = [],
	title,
	...note
}: NotePopulated): NotePopulated => ({
	...note,
	title: decodeHtml(title),
	content: content.map(decodeNoteContent),
});
